import { Chart as ChartJS, type Plugin } from 'chart.js/auto';
import annotationPlugin from 'chartjs-plugin-annotation';
import 'chartjs-adapter-date-fns';
import {
  getAnnotations,
  getBasicChartOptions,
  getDatasets,
  getGraphData,
} from '../../components/CommitDetailsGraph';
import type { CommitDetails, Regression } from '../../interfaces/CommitDetails';
import type { Service } from '../../interfaces/Service';
import { COMMIT, MetricNames } from '../../utils/constants';
import type { RegressionEmailInlineImage } from './create-regression-email';

ChartJS.register(annotationPlugin);

const MAX_REGRESSION_CHARTS = 3;
const PRIORITY_PROCESS_NAME = 'contentapppool';
const PRIORITY_ENVIRONMENTS = ['msit', 'spdf'];

const whiteBackgroundPlugin: Plugin<'line'> = {
  id: 'regressionEmailWhiteBackground',
  beforeDraw(chart) {
    const { ctx, width, height } = chart;
    ctx.save();
    ctx.globalCompositeOperation = 'destination-over';
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, width, height);
    ctx.restore();
  },
};

function getProcesses(service: Service | undefined) {
  return service?.processes
    ? Object.values(service.processes)
    : service?.Processes ?? [];
}

function getRegressionPriority(
  service: Service | undefined,
  regression: Regression,
): number {
  const processName = getProcessName(service, regression.processId)
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');
  if (processName !== PRIORITY_PROCESS_NAME) return PRIORITY_ENVIRONMENTS.length + 1;

  const environmentName = getEnvironmentName(service, regression.environmentId).toLowerCase();
  const environmentPriority = PRIORITY_ENVIRONMENTS.indexOf(environmentName);
  return environmentPriority >= 0 ? environmentPriority : PRIORITY_ENVIRONMENTS.length;
}

export function selectRegressionChartRegressions(
  regressions: Regression[],
  service: Service | undefined,
): Regression[] {
  const uniqueRegressions = new Map<string, Regression>();

  for (const regression of regressions) {
    if (!regression.isRegression || regression.source !== 2) continue;

    const key = `${regression.environmentId}:${regression.processId}:${regression.metricType}`;
    uniqueRegressions.set(key, regression);
  }

  return [...uniqueRegressions.values()]
    .map((regression, originalIndex) => ({ regression, originalIndex }))
    .sort((left, right) => {
      const priorityDifference = getRegressionPriority(service, left.regression)
        - getRegressionPriority(service, right.regression);
      return priorityDifference || left.originalIndex - right.originalIndex;
    })
    .slice(0, MAX_REGRESSION_CHARTS)
    .map(({ regression }) => regression);
}

function getEnvironmentName(service: Service | undefined, environmentId: number): string {
  const environments = service?.environments
    ? Object.values(service.environments)
    : service?.Environments ?? [];
  return environments.find(environment => environment.id === environmentId)?.name
    ?? `Environment ${environmentId}`;
}

function getProcessName(service: Service | undefined, processId: number): string {
  const process = getProcesses(service).find(candidate => candidate.id === processId);
  return process?.friendlyName || process?.name || `Process ${processId}`;
}

function createChartImage(
  commitDetails: CommitDetails,
  service: Service | undefined,
  regression: Regression,
  index: number,
): RegressionEmailInlineImage {
  const details = getGraphData(
    commitDetails,
    regression.environmentId,
    regression.processId,
    COMMIT,
    regression.metricType,
    false,
  );
  const datasets = getDatasets(
    details,
    commitDetails,
    regression.environmentId,
    COMMIT,
    regression.metricType,
  );

  if (datasets.length === 0 || datasets.every(dataset => dataset.data?.length === 0)) {
    throw new Error(
      `Could not render the regression graph for environment ${regression.environmentId}, `
      + `process ${regression.processId}, metric ${regression.metricType}.`,
    );
  }

  const canvas = document.createElement('canvas');
  canvas.width = 1200;
  canvas.height = 600;

  const options = getBasicChartOptions(regression.metricType, COMMIT, true);
  options.responsive = false;
  options.maintainAspectRatio = false;
  options.animation = false;
  options.events = [];
  options.devicePixelRatio = 1;
  options.plugins.annotation = {
    annotations: getAnnotations(commitDetails, regression.environmentId),
  };

  const chart = new ChartJS(canvas, {
    type: 'line',
    data: { datasets },
    options,
    plugins: [whiteBackgroundPlugin],
  });

  chart.update('none');
  const dataUrl = canvas.toDataURL('image/png');
  chart.destroy();

  const base64Marker = 'base64,';
  const markerIndex = dataUrl.indexOf(base64Marker);
  if (markerIndex < 0) {
    throw new Error('Could not encode a regression graph as a PNG image.');
  }

  const metricName = MetricNames[regression.metricType] ?? `Metric ${regression.metricType}`;
  const environmentName = getEnvironmentName(service, regression.environmentId);
  const processName = getProcessName(service, regression.processId);
  const caption = `${metricName} regression - ${environmentName} - ${processName}`;

  return {
    contentId: `regression-chart-${index + 1}@prguardian`,
    fileName: `regression-chart-${index + 1}.png`,
    contentType: 'image/png',
    base64Content: dataUrl.slice(markerIndex + base64Marker.length),
    caption,
    altText: caption,
  };
}

export async function createRegressionChartImages(
  commitDetails: CommitDetails,
  service: Service | undefined,
): Promise<RegressionEmailInlineImage[]> {
  const regressions = selectRegressionChartRegressions(commitDetails.regressions ?? [], service);
  if (regressions.length === 0) {
    throw new Error('No detected regression graphs are available to include in the email draft.');
  }

  return regressions.map((regression, index) =>
    createChartImage(commitDetails, service, regression, index));
}
